import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { KeyRound, Copy, Check, Ban, RotateCcw, MessageCircle, RefreshCw } from 'lucide-react';

const PLANS = [
  { value: 'mensal', label: '30 dias (mensal)' },
  { value: 'trimestral', label: '90 dias (trimestral)' },
  { value: 'anual', label: '365 dias (anual)' },
  { value: 'vitalicio', label: 'Sem vencimento (vitalícia)' },
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const STATE = {
  ativa: 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
  vencida: 'bg-amber-900/50 text-amber-300 border-amber-700',
  desativada: 'bg-red-900/50 text-red-300 border-red-700',
  renovada: 'bg-slate-800 text-slate-300 border-slate-600',
};
const inputCls = 'bg-slate-800 border-slate-700 text-white';

// Dia 10 do próximo mês (ou do mês seguinte a uma data-base) — padrão de cobrança do fornecedor
const nextDay10 = (base = new Date()) => {
  const d = new Date(base.getFullYear(), base.getMonth() + 1, 10, 12);
  if (d <= new Date()) d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-10`;
};

// Seletor de validade compartilhado (gerar e renovar): prazo em dias OU data exata
const ValidityPicker = ({ value, onChange, idPrefix }) => (
  <div className="space-y-2">
    <div className="flex gap-1 text-xs">
      {[['plan', 'Prazo'], ['date', 'Data exata']].map(([m, l]) => (
        <button key={m} type="button" onClick={() => onChange({ ...value, mode: m })} className={`px-2 py-1 rounded border ${value.mode === m ? 'bg-emerald-700 border-emerald-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`} data-testid={`${idPrefix}-mode-${m}`}>{l}</button>
      ))}
    </div>
    {value.mode === 'plan' ? (
      <select value={value.plan} onChange={(e) => onChange({ ...value, plan: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-700 bg-slate-800 text-white text-sm" data-testid={`${idPrefix}-plan`}>
        {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    ) : (
      <div className="flex gap-2">
        <Input type="date" value={value.expires_on} onChange={(e) => onChange({ ...value, expires_on: e.target.value })} className={inputCls} data-testid={`${idPrefix}-date`} />
        <Button type="button" variant="outline" onClick={() => onChange({ ...value, expires_on: nextDay10() })} className="border-slate-700 bg-slate-800 text-emerald-200 hover:bg-slate-700 whitespace-nowrap" title="Dia 10 do próximo mês" data-testid={`${idPrefix}-day10`}>Dia 10</Button>
      </div>
    )}
  </div>
);
const validityPayload = (v) => (v.mode === 'date' ? { expires_on: v.expires_on } : { plan: v.plan });

const waText = (k) => encodeURIComponent(
  `Olá! Segue a chave de liberação do Smart Flow (${k.client_name}).\n` +
  `Validade: ${k.days > 0 ? `${k.days} dias (até ${fmtDate(k.expires_at)})` : 'sem vencimento'}\n\n` +
  `No sistema, entre como admin → tela "Liberar Sistema" → passo 3 → cole a chave e clique em Ativar:\n\n${k.blob}`
);

export const LicenseKeysPanel = ({ api, hdr }) => {
  const [form, setForm] = useState({ client_name: '', cnpj: '', fingerprint: '', notes: '' });
  const [validity, setValidity] = useState({ mode: 'date', plan: 'mensal', expires_on: nextDay10() });
  const [renewing, setRenewing] = useState(null); // { key, validity }
  const [keys, setKeys] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [filter, setFilter] = useState('');
  const [health, setHealth] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/license-admin/list', hdr); setKeys(data.keys || []); }
    catch (e) { setError(e.response?.data?.detail || 'Erro ao carregar chaves'); }
    try { const { data } = await api.get('/license-admin/health', hdr); setHealth(data); }
    catch (e) { setHealth({ ok: false, error: e.response?.data?.detail || 'Não foi possível verificar a chave privada do servidor' }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const { data } = await api.post('/license-admin/generate', { ...form, ...validityPayload(validity) }, hdr);
      if (!data || typeof data !== 'object' || !data.blob) {
        throw new Error('O servidor não devolveu a chave. Verifique se o backend foi atualizado e se LICENSE_PRIVATE_KEY_B64 está configurada (veja o aviso acima).');
      }
      setGenerated(data);
      setForm({ client_name: '', cnpj: '', fingerprint: '', notes: '' });
      load();
    } catch (err) { setError(err.response?.data?.detail || err.message || 'Erro ao gerar chave'); }
    finally { setBusy(false); }
  };

  const startRenew = (k) => setRenewing({ key: k, validity: { mode: 'date', plan: 'mensal', expires_on: nextDay10(k.expires_at ? new Date(k.expires_at) : new Date()) } });

  const renew = async () => {
    if (!renewing) return;
    setError(''); setBusy(true);
    try {
      const { data } = await api.post(`/license-admin/${renewing.key.id}/renew`, { ...validityPayload(renewing.validity), notes: `Renovação (${renewing.key.client_name})` }, hdr);
      if (!data?.blob) throw new Error('O servidor não devolveu a chave renovada.');
      setGenerated(data);
      setRenewing(null);
      load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) { setError(err.response?.data?.detail || err.message || 'Erro ao renovar chave'); }
    finally { setBusy(false); }
  };

  const copy = async (text, id) => {
    try { await navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(''), 1500); } catch { /* noop */ }
  };

  const toggle = async (k) => {
    const action = k.status === 'revoked' ? 'reactivate' : 'revoke';
    if (action === 'revoke' && !window.confirm(`Desativar a chave de "${k.client_name}"?\n\nO sistema do cliente vai bloquear na próxima consulta à nuvem (até 24h).`)) return;
    try { await api.post(`/license-admin/${k.id}/${action}`, {}, hdr); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Erro ao alterar chave'); }
  };

  const shown = keys.filter((k) => !filter || `${k.client_name} ${k.cnpj} ${k.fingerprint}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="license-keys-panel">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-1 text-emerald-400"><KeyRound size={18} /><h2 className="text-lg font-bold text-white">Gerar chave de licença</h2></div>
        <p className="text-xs text-slate-400 mb-4">O cliente lê o <b>fingerprint</b> na tela "Liberar Sistema" (passo 2) e te envia. A chave gerada só funciona naquela máquina.</p>
        {health && (
          <div className={`mb-4 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${health.ok ? 'bg-emerald-950/40 border-emerald-800 text-emerald-200' : 'bg-red-950/50 border-red-800 text-red-200'}`} data-testid="lic-health">
            <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${health.ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
            <span>{health.ok ? 'Chave privada do fornecedor carregada e válida — pronto para gerar chaves.' : <><b>Chave privada não disponível no servidor:</b> {health.error}</>}</span>
          </div>
        )}
        <form onSubmit={generate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1"><Label className="text-slate-300">Nome do cliente *</Label><Input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls} placeholder="Ex: Verdurão Centro" data-testid="lic-client-name" /></div>
          <div className="space-y-1"><Label className="text-slate-300">CNPJ *</Label><Input required value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className={inputCls} placeholder="00.000.000/0001-00" data-testid="lic-cnpj" /></div>
          <div className="space-y-1"><Label className="text-slate-300">Fingerprint *</Label><Input required value={form.fingerprint} onChange={(e) => setForm({ ...form, fingerprint: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX" data-testid="lic-fingerprint" /></div>
          <div className="space-y-1">
            <Label className="text-slate-300">Validade *</Label>
            <ValidityPicker value={validity} onChange={setValidity} idPrefix="lic" />
          </div>
          <div className="space-y-1"><Label className="text-slate-300">Observações</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} placeholder="Ex: renovação, reinstalação..." data-testid="lic-notes" /></div>
          <div className="flex items-end"><Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="lic-generate"><KeyRound size={16} className="mr-2" />{busy ? 'Gerando...' : 'Gerar Chave'}</Button></div>
        </form>
        {error && <div className="mt-3"><Alert variant="destructive"><AlertDescription data-testid="lic-error">{error}</AlertDescription></Alert></div>}

        {generated && (
          <div className="mt-5 bg-emerald-950/40 border border-emerald-800 rounded-lg p-4" data-testid="lic-generated">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-emerald-200 font-bold">Chave gerada para <span className="text-white">{generated.client_name}</span> · {generated.plan !== 'vitalicio' ? `válida até ${fmtDate(generated.expires_at)}` : 'sem vencimento'}</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => copy(generated.blob, 'gen')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lic-copy-generated">{copied === 'gen' ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}{copied === 'gen' ? 'Copiada!' : 'Copiar chave'}</Button>
                <a href={`https://wa.me/?text=${waText(generated)}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-emerald-700 bg-slate-800 text-emerald-200 hover:bg-slate-700" data-testid="lic-whatsapp"><MessageCircle size={14} className="mr-1" /> WhatsApp</Button></a>
              </div>
            </div>
            <textarea readOnly value={generated.blob} rows={4} onClick={(e) => e.target.select()} className="w-full bg-slate-950 border border-slate-700 rounded p-2 font-mono text-[11px] text-emerald-100 break-all" data-testid="lic-generated-blob" />
          </div>
        )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-white">Chaves emitidas <span className="text-slate-500 text-sm font-normal">({keys.length})</span></h2>
          <div className="flex gap-2">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por cliente, CNPJ ou fingerprint" className={`${inputCls} w-72`} data-testid="lic-filter" />
            <Button variant="outline" onClick={load} className="border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700" data-testid="lic-refresh"><RefreshCw size={16} /></Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lic-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <tr><th className="p-2 text-left">Cliente</th><th className="p-2 text-left">CNPJ</th><th className="p-2 text-left">Fingerprint</th><th className="p-2 text-left">Emissão</th><th className="p-2 text-left">Vencimento</th><th className="p-2 text-center">Status</th><th className="p-2 text-right">Ações</th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500" data-testid="lic-empty">Nenhuma chave emitida.</td></tr>}
              {shown.map((k) => (
                <React.Fragment key={k.id}>
                <tr className="border-t border-slate-800" data-testid={`lic-row-${k.id}`}>
                  <td className="p-2 font-bold text-white">{k.client_name}{k.notes ? <div className="text-[11px] text-slate-500 font-normal">{k.notes}</div> : null}</td>
                  <td className="p-2 text-slate-300">{fmtCnpj(k.cnpj)}</td>
                  <td className="p-2 font-mono text-xs text-slate-300">{k.fingerprint}</td>
                  <td className="p-2 text-slate-300">{fmtDate(k.issued_at)}</td>
                  <td className="p-2" data-testid={`lic-expires-${k.id}`}>
                    {k.days > 0 && k.plan !== 'vitalicio'
                      ? <><span className={`font-bold ${k.state === 'vencida' ? 'text-amber-300' : k.days_remaining != null && k.days_remaining <= 7 && k.state === 'ativa' ? 'text-amber-300' : 'text-white'}`}>{fmtDate(k.expires_at)}</span>
                          {k.state === 'ativa' && k.days_remaining != null ? <div className="text-[11px] text-slate-500">{k.days_remaining} dia(s)</div> : null}
                          {k.state === 'vencida' ? <div className="text-[11px] text-amber-400">vencida há {Math.abs(k.days_remaining)} dia(s)</div> : null}</>
                      : <span className="text-slate-300">Sem vencimento</span>}
                  </td>
                  <td className="p-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase ${STATE[k.state]}`} data-testid={`lic-state-${k.id}`}>{k.state}</span></td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {k.blob && <Button size="sm" variant="ghost" onClick={() => copy(k.blob, k.id)} className="text-slate-300 hover:text-white hover:bg-slate-800" title="Copiar chave" data-testid={`lic-copy-${k.id}`}>{copied === k.id ? <Check size={14} /> : <Copy size={14} />}</Button>}
                    {k.state !== 'renovada' && <Button size="sm" variant="ghost" onClick={() => startRenew(k)} className="text-sky-300 hover:text-white hover:bg-sky-900/50" data-testid={`lic-renew-${k.id}`}><RefreshCw size={14} className="mr-1" /> Renovar</Button>}
                    {k.status === 'revoked'
                      ? <Button size="sm" variant="ghost" onClick={() => toggle(k)} className="text-emerald-300 hover:text-white hover:bg-emerald-900/50" data-testid={`lic-reactivate-${k.id}`}><RotateCcw size={14} className="mr-1" /> Reativar</Button>
                      : <Button size="sm" variant="ghost" onClick={() => toggle(k)} className="text-red-300 hover:text-white hover:bg-red-900/50" data-testid={`lic-revoke-${k.id}`}><Ban size={14} className="mr-1" /> Desativar</Button>}
                  </td>
                </tr>
                {renewing?.key.id === k.id && (
                  <tr className="bg-sky-950/30" data-testid={`lic-renew-form-${k.id}`}>
                    <td colSpan={7} className="p-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="text-sm text-sky-100"><b>Renovar {k.client_name}</b><div className="text-xs text-slate-400">Nova chave para o mesmo CNPJ/fingerprint. Vencimento atual: {k.days > 0 ? fmtDate(k.expires_at) : 'sem vencimento'}</div></div>
                        <div className="w-72"><ValidityPicker value={renewing.validity} onChange={(v) => setRenewing({ ...renewing, validity: v })} idPrefix="renew" /></div>
                        <Button onClick={renew} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="renew-confirm"><KeyRound size={14} className="mr-1" />{busy ? 'Gerando...' : 'Gerar chave renovada'}</Button>
                        <Button variant="ghost" onClick={() => setRenewing(null)} className="text-slate-300 hover:bg-slate-800" data-testid="renew-cancel">Cancelar</Button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Ao desativar, o sistema do cliente consulta a nuvem 1x por dia e entra na tela de bloqueio. Sem internet ele continua até o vencimento da chave.</p>
      </div>
    </div>
  );
};

export default LicenseKeysPanel;
